import { AlertTypes, DeviceTypes, EventTypes, ReportTypes, UserTypes } from "../types";
import { getRoundedLatLngString } from "../utils/roundOffLatLng";


const locationPrecision = 3;

export namespace EventFactory {
    export const transformAlertsAndDevices = (
        alerts: AlertTypes.Model[],
        devices: DeviceTypes.Model[],
        reports: ReportTypes.Model[],
        users: UserTypes.Model[],
        filter?: { alerts: boolean, devices: boolean, reports: boolean, safetyReport: boolean }
    ) => {
        const events: Array<EventTypes.Model> = [];
        const alertsToEventsMap =
            alerts?.map(
                (alert) =>
                ({
                    type: 'alert',
                    content: alert,
                    latitude: +alert?.locations[0]?.latitude!,
                    longitude: +alert?.locations[0]?.longitude!,
                    latLngString: getRoundedLatLngString(+alert?.locations[0]?.latitude!, +alert?.locations[0]?.longitude!),
                    latLngStringApprox: getRoundedLatLngString(+alert?.locations[0]?.latitude!, +alert?.locations[0]?.longitude!, locationPrecision),
                    isPrecise: alert?.locations[0]?.isPrecise,
                    appxDistMeters: alert?.locations[0]?.appxDistMeters,
                    userName: `${alert?.user?.firstName} ${alert?.user?.lastName}`,
                    addressLocation: alert?.address,
                    lastUpdate: alert?.updatedAt ? alert?.updatedAt : alert?.createdAt
                } as EventTypes.Model)
            ) || [];

        const safetySearchReportsToEventsMap: EventTypes.Model[] = []
        const reportsToEventsMap: EventTypes.Model[] = []

        reports?.map((report) => {
            let reportItem = {
                type: 'report',
                content: report,
                latitude: +report?.latitude,
                longitude: +report?.longitude,
                latLngString: getRoundedLatLngString(+report?.latitude, +report?.longitude),
                latLngStringApprox: getRoundedLatLngString(+report?.latitude, +report?.longitude, locationPrecision),
                isPrecise: true,
                userName: `${report?.user?.firstName} ${report?.user?.lastName}`,
                addressLocation: report?.address,
                lastUpdate: report?.updatedAt ? report?.updatedAt : report?.createdAt
            } as EventTypes.Model
            // Safety Reports = reportType is 'Safety Report' OR 'Intake Risk Assessment'
            // Regular Reports = everything else
            if (report?.reportType === 'Safety Report' || report?.reportType === 'Intake Risk Assessment') {
                reportItem.type = 'safetyReport'
                safetySearchReportsToEventsMap.push(reportItem)
            } else {
                reportsToEventsMap.push(reportItem)
            }
        })

        // Create a map of user IDs to their mobile locations for deduplication
        const userMobileLocationMap = new Map<string, { lat: number, lng: number }>();
        
        const usersMobileLocationEventsMap = users?.filter(
            u => u?.mobileLocation &&
                u?.mobileLocation?.latitude &&
                u?.mobileLocation?.longitude &&
                u?.firstName
        )?.map((user) => {
            // Store user's mobile location for device deduplication
            if (user?.id) {
                userMobileLocationMap.set(user.id, {
                    lat: +user?.mobileLocation?.latitude!,
                    lng: +user?.mobileLocation?.longitude!
                });
            }
            return {
                type: 'userMobileLocation',
                content: user,
                latitude: +user?.mobileLocation?.latitude!,
                longitude: +user?.mobileLocation?.longitude!,
                latLngString: getRoundedLatLngString(+user?.mobileLocation?.latitude!, +user?.mobileLocation?.longitude!),
                latLngStringApprox: getRoundedLatLngString(+user?.mobileLocation?.latitude!, +user?.mobileLocation?.longitude!, locationPrecision),
                isPrecise: true,
                userName: `${user?.firstName} ${user?.lastName}`,
                addressLocation: user?.mobileLocation?.address,
                lastUpdate: user?.mobileLocation?.updatedAt ? user?.mobileLocation?.updatedAt : user?.updatedAt
            } as EventTypes.Model;
        }) || [];

        // Helper to check if two locations are the same (using rounded lat/lng for comparison)
        const isSameLocation = (lat1: number, lng1: number, lat2: number, lng2: number): boolean => {
            const rounded1 = getRoundedLatLngString(lat1, lng1, locationPrecision);
            const rounded2 = getRoundedLatLngString(lat2, lng2, locationPrecision);
            return rounded1 === rounded2;
        };

        // Filter out devices that are at the same location as their user's mobile location
        // Only show device if it's at a DIFFERENT location than the user
        const devicesToEventsMap = devices?.filter((device) => {
            if (!device?.userId || !device?.lastLocation?.lat || !device?.lastLocation?.lng) {
                return true; // Include device if no user or no location
            }
            
            const userMobileLocation = userMobileLocationMap.get(device.userId);
            if (!userMobileLocation) {
                return true; // Include device if user has no mobile location
            }
            
            // Only include device if it's at a DIFFERENT location than user's mobile location
            return !isSameLocation(
                device.lastLocation.lat,
                device.lastLocation.lng,
                userMobileLocation.lat,
                userMobileLocation.lng
            );
        })?.map(
            (device) =>
            ({
                type: 'device',
                content: device,
                latitude: device?.lastLocation?.lat,
                longitude: device?.lastLocation?.lng,
                latLngString: getRoundedLatLngString(device?.lastLocation?.lat, device?.lastLocation?.lng),
                latLngStringApprox: getRoundedLatLngString(device?.lastLocation?.lat, device?.lastLocation?.lng, locationPrecision),
                isPrecise: device?.isPrecise,
                userName: `${device?.user?.firstName} ${device?.user?.lastName}`,
                lastUpdate: device?.updatedAt ? device?.updatedAt : device?.createdAt,
                addressLocation: device?.lastLocation?.address,
                appxDistMeters: device?.appxDistMeters,
            } as EventTypes.Model)
        ) || [];

        [
            ...alertsToEventsMap,
            ...devicesToEventsMap,
            ...reportsToEventsMap,
            ...safetySearchReportsToEventsMap,
            ...usersMobileLocationEventsMap,
        ]?.forEach((event) => {
            if (event.type === 'alert' && filter?.alerts) {
                events.push(event);
            }

            if (event.type === 'userMobileLocation' && filter?.devices) {
                events.push(event);
            }

            if (event.type === 'device' && filter?.devices) {
                events.push(event);
            }

            if (event.type === 'report' && filter?.reports) {
                events.push(event);
            }

            if (event.type === 'safetyReport' && filter?.safetyReport) {
                events.push(event);
            }
        });

        return events;
    }
}